import { useDarkMode } from '../DarkModeContext';
import useIsVisible from '../isVisible';
import { useRef } from 'react';

const Chip = ({ text, dark }) => (
  <span className={`inline-block px-3 py-1 text-xs rounded-full border mr-2 mb-2 font-mono ${
    dark
      ? 'bg-Template-1/20 border-Template-2/40 text-Template-2'
      : 'bg-Template-0/10 border-Template-1/40 text-Template-0'
  }`}>{text}</span>
);

const StateCard = ({ name, transitions, desc, isDarkMode }) => (
  <div className={`rounded-xl border p-4 ${
    isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-Template-2 shadow-sm'
  }`}>
    <div className={`font-mono font-bold text-sm mb-1 ${isDarkMode ? 'text-Template-2' : 'text-Template-0'}`}>{name}</div>
    <p className={`text-xs leading-relaxed mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{desc}</p>
    {transitions && (
      <div className="flex flex-wrap gap-1">
        {transitions.map(t => (
          <span key={t} className={`text-xs px-2 py-0.5 rounded-full ${
            isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-500'
          }`}>→ {t}</span>
        ))}
      </div>
    )}
  </div>
);

const PhaseRow = ({ name, desc, isDarkMode }) => (
  <div className={`flex gap-3 items-start py-2 border-b last:border-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`}>
    <span className={`font-mono text-xs mt-0.5 flex-shrink-0 px-2 py-0.5 rounded ${
      isDarkMode ? 'bg-Template-1/30 text-Template-2' : 'bg-Template-0/10 text-Template-0'
    }`}>{name}</span>
    <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{desc}</p>
  </div>
);

function Software() {
  const { isDarkMode } = useDarkMode();

  const refs = [useRef(), useRef(), useRef()];
  const vis = [
    useIsVisible(refs[0]),
    useIsVisible(refs[1]),
    useIsVisible(refs[2]),
  ];

  const sec = (i) => `px-8 py-12 transition-opacity ease-in duration-700 ${vis[i] ? 'opacity-100' : 'opacity-0'} ${
    i % 2 === 0
      ? isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
      : isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
  }`;

  const h2 = `text-4xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-Template-0'}`;
  const hr = `border-t-2 my-4 ${isDarkMode ? 'border-gray-600' : 'border-Template-2'}`;
  const body = `text-base leading-relaxed mb-6 max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`;

  const states = [
    {
      name: 'StateInit',
      desc: 'Bootet den Würfel: deaktiviert WiFi, initialisiert alle Module (IMU, LEDs, BLE, Battery). Prüft Akkuladestand.',
      transitions: ['StateChargingActive (Akku geladen)'],
    },
    {
      name: 'StateActive',
      desc: 'Normalbetrieb. IMU-Interrupts aktiv, BLE verbunden, LEDs zeigen das Ergebnis. Verarbeitet den Würfelwurf in 4 Phasen (siehe unten).',
      transitions: ['StateChargingActive (Laden erkannt)', 'StateInactive (50 s kein Wurf)'],
    },
    {
      name: 'StateInactive',
      desc: 'Keine Aktivität. Peripherie deaktiviert, nur IMU-Interrupt aktiv.',
      transitions: ['StateActive (Bewegung erkannt)', 'StateDeepSleep (20 s Inaktivität)'],
    },
    {
      name: 'StateChargingActive',
      desc: 'Würfel liegt auf dem Ladegerät. BLE bleibt aktiv für Konfiguration. Peripherie wird nach 60 s deaktiviert.',
      transitions: ['StateActive (Laden beendet)'],
    },
    {
      name: 'StateDeepSleep',
      desc: 'ESP32 im Deep Sleep. BLE deaktiviert. Aufwachen nur durch IMU-GPIO-Interrupt.',
      transitions: ['StateActive (Bewegung erkannt)'],
    },
  ];

  const rollPhases = [
    { name: 'Idle', desc: 'Würfel liegt ruhig. LEDs aus. Wartet auf IMU-Interrupt (INT1 = Flying).' },
    { name: 'Flying', desc: 'Würfel ist in der Luft. Wartet auf IMU-Interrupt (INT2 = Casted).' },
    { name: 'Resolve', desc: 'Würfel hat aufgehört zu rollen. Liest bis zu 10× alle 50 ms den Gravitationsvektor. Erste konfidente Seite gewinnt.' },
    { name: 'Display', desc: 'Ergebnis steht fest. Zahl wird auf der oberen Seite in der konfigurierten Farbe angezeigt. Hält 10 Sekunden.' },
  ];

  return (
    <>
      <div ref={refs[0]} className={sec(0)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Architektur</h2>
          <hr className={hr} />
          <p className={body}>
            Die Firmware basiert auf einer <strong>State Machine</strong> mit 5 Zuständen.
            Übergänge werden durch IMU-Interrupts (Bewegung / Stillstand) und den Ladestatus
            des Akkus ausgelöst.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
            {states.map(s => (
              <StateCard key={s.name} {...s} isDarkMode={isDarkMode} />
            ))}
          </div>

          <h3 className={`text-xl font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-Template-0'}`}>
            StateActive – Roll-Phasen
          </h3>
          <p className={`text-sm leading-relaxed mb-3 max-w-2xl ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Der gesamte Ablauf eines Würfelwurfs läuft intern als 4-Phasen-Zyklus in StateActive:
          </p>
          <div className={`rounded-xl border max-w-2xl p-4 ${isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-Template-2 shadow-sm'}`}>
            {rollPhases.map(p => (
              <PhaseRow key={p.name} {...p} isDarkMode={isDarkMode} />
            ))}
          </div>
        </div>
      </div>

      <div ref={refs[1]} className={sec(1)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>ESP32-C3</h2>
          <hr className={hr} />
          <p className={body}>
            Der <strong>ESP32-C3 Mini-1</strong> läuft mit <strong>FreeRTOS</strong> und ESP-IDF.
            Power-Management ist zentral: <strong>Automatic Frequency Scaling (AFS)</strong> passt
            die CPU-Frequenz (10–80 MHz) dynamisch an. BLE hält einen Lock auf Light Sleep;
            im StateDeepSleep geht der ESP32 in echten Deep Sleep mit GPIO-Wakeup.
          </p>
          <div className="flex flex-wrap mb-4">
            <Chip text="ESP-IDF" dark={isDarkMode} />
            <Chip text="FreeRTOS" dark={isDarkMode} />
            <Chip text="AFS 10–80 MHz" dark={isDarkMode} />
            <Chip text="Light Sleep" dark={isDarkMode} />
            <Chip text="GPIO Wakeup" dark={isDarkMode} />
            <Chip text="BLE 5.0" dark={isDarkMode} />
            <Chip text="PlatformIO" dark={isDarkMode} />
          </div>
        </div>
      </div>

      <div ref={refs[2]} className={sec(2)}>
        <div className="max-w-4xl mx-auto">
          <h2 className={h2}>Module</h2>
          <hr className={hr} />
          <p className={`text-base leading-relaxed mb-6 max-w-3xl ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Die Firmware ist in unabhängige Module unterteilt. Jedes Modul wird separat entwickelt
            und getestet; die Integration erfolgt über die State Machine.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                title: 'Gyros (IMU)',
                chips: ['LSM6DSV', 'SFLP', 'Lookup Table'],
                desc: 'Gravitationsvektor aus FIFO. Vergleich mit 20 Flächennormalen per Lookup Table.',
              },
              {
                title: 'LED (CubeDisplay)',
                chips: ['FastLED', 'RGB', 'Pro Side'],
                desc: 'Adressierbare RGB-LEDs. showNumberOnFace() zeigt Ergebnis auf der oberen Seite.',
              },
              {
                title: 'BLE',
                chips: ['NimBLE', 'GATT', 'iOS App'],
                desc: 'GATT Service mit Config- und Roll-Characteristic. Sendet Würfelergebnis und empfängt Konfigurationsänderungen.',
              },
            ].map((m) => (
              <div key={m.title} className={`rounded-xl border p-4 ${
                isDarkMode ? 'bg-gray-700/50 border-gray-600' : 'bg-white border-Template-2 shadow-sm'
              }`}>
                <div className={`font-bold mb-2 ${isDarkMode ? 'text-Template-2' : 'text-Template-0'}`}>{m.title}</div>
                <div className="flex flex-wrap mb-2">
                  {m.chips.map(c => <Chip key={c} text={c} dark={isDarkMode} />)}
                </div>
                <p className={`text-xs leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{m.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default Software;
